import React from 'react';

/**
 * Marks the score as AI-derived. Sized locally rather than reusing the tile's
 * `AiSparkle`, whose dimensions are baked into its class string — with
 * `important: true` in the Tailwind config, a size passed in would tie on
 * specificity and lose.
 */
const SparkleIcon = ({ className }) => (
    <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className={className}
    >
        <path d="M12 0c.9 6.6 4.4 10.1 11 11-6.6.9-10.1 4.4-11 11-.9-6.6-4.4-10.1-11-11C7.6 10.1 11.1 6.6 12 0Z" />
    </svg>
);

/**
 * The AI's read of a talent's resume against the role, shown alongside the
 * match breakdown but deliberately outside it.
 *
 * Carries the brand purple and a sparkle so it reads as an AI signal at a
 * glance, distinct from the criteria panels beside it, which are scored from
 * the talent's profile.
 *
 * Shared by the profile section and the match-details modal so the treatment
 * and wording can't drift between them.
 */
const AiResumeScoreCallout = ({ score, className }) => {
    if (score === null || score === undefined) {
        return null;
    }

    return (
        <div className={`relative flex flex-row items-center gap-4 overflow-hidden rounded-xl border border-primary-lightest bg-gradient-to-r from-[#F6F2FF] via-[#FBFAFF] to-white p-4 ${className || ''}`}>
            {/* Oversized watermark glyph, clipped by the container — signals
                "AI" without another element competing for attention. */}
            <SparkleIcon className="pointer-events-none absolute -right-4 -top-5 h-24 w-24 text-primary/[0.06]" />

            <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white shadow-[inset_0_0_0_2px_#5F3DB1]">
                <span className="text-2xl font-extrabold tabular-nums leading-none tracking-[-0.02em] text-primary">
                    {score}
                </span>
            </div>

            <div className="relative min-w-0">
                <p className="mb-0 inline-flex items-center gap-1.5 text-base font-bold text-[#0E0E14]">
                    <SparkleIcon className="h-3.5 w-3.5 shrink-0 text-primary" />
                    AI Resume Score
                </p>

                <p className="mb-0 text-sm leading-[1.45] text-[#595D68]">
                    How this talent’s resume reads against the role, scored by AI.
                </p>
            </div>
        </div>
    );
};

export default AiResumeScoreCallout;
